import * as Sync from 'twilio-sync'
import isEmpty from 'lodash/isEmpty'
import { PlayInOptions, PlayInStatus, PlayInObject, PlayInRemoteStatus } from './types'
import { addEntry } from '../audit'
import getEnvironment from '@bt-react/libs/getEnvironment'

export const defaultState: PlayInObject = {
  currentPos: 0,
  currentPosTimestamp: '',
  endAt: null,
  initiatedBy: null,
  pausedAt: null,
  remoteStatusRequest: null,
  startAt: 0,
  status: PlayInStatus.EMPTY,
  url: '',
  sources: [],
  latestAppliedRequest: 0
}

class PlayIn {

  private _doc: Sync.SyncDocument

  constructor (doc) {

    this._doc = doc as Sync.SyncDocument

  }

  get doc (): PlayInObject {

    return { ...this._doc.data } as PlayInObject

  }

  /** Determines whether the playIn is ready to play */
  isReady (): boolean {

    const { status } = this.doc as PlayInObject
    return status === PlayInStatus.READY

  }

  /** Determines whether the playIn is playing */
  isPlaying (): boolean {

    const { status } = this.doc as PlayInObject
    return status === PlayInStatus.PLAYING

  }

  /** Determines whether the PlayIn is paused */
  isPaused (): boolean {

    const { status } = this.doc as PlayInObject
    return status === PlayInStatus.PAUSED

  }

  /** Determines whether the PlayIn is inactive */
  isInactive (): boolean {

    const { status } = this.doc as PlayInObject
    return status === PlayInStatus.INACTIVE

  }

  /** Is PlayIn currently the focused media */
  isFocused (): boolean {

    const { status } = this.doc as PlayInObject
    return status !== PlayInStatus.INACTIVE && status !== PlayInStatus.EMPTY

  }

  getUrlFromSources( sources ){

    // Bridge unable to access the mp4s from cloudfront in int environments - so there we use m3u8
    const environment = getEnvironment(true).domain

    if (environment.includes('int05.brighttalk.net') || environment.includes('test01.brighttalk.net')) {

      return sources.find( source => /.m3u8$/.test( source ) ) || sources[ 0 ]

    }
    return sources[ 0 ]
  }
  /**
   * Sets the playIn information within the class
   * @param sources Array of urls for videos
   * @param [options={}] An options object
   */
  async init (sources: string[], options: PlayInOptions = {}): Promise<boolean> {

    const {
      startAt = 0,
      endAt = null,
      initiatedBy = null,
      pausedAt = null
    } = options
    const date = new Date(Date.now()).toISOString().substr(0, 19)
    const values: PlayInObject = {
      ...defaultState,
      currentPos: startAt || 0,
      currentPosTimestamp: date,
      endAt,
      initiatedBy,
      pausedAt,
      startAt,
      status: PlayInStatus.PLAYING,
      url : this.getUrlFromSources( sources ),
      sources
    }

    addEntry('WatchService', 'PlayInStarted', 'Play-In initiated', 'info')

    try {

      await this._doc.set(values)
      return true

    }
    catch (e) {

      addEntry('WatchService', 'PlayInGettingUrlFromSources', `Error getting url from sources: ${e}.`)
      return false

    }

  }

  /**
   * Starts the playIn
   * @param startAt [startAt=0] The position - in seconds - to start the video at
   */
  async start (startAt = 0): Promise<boolean> {

    const date = new Date(Date.now()).toISOString().substr(0, 19)
    const update = {
      startAt,
      status: PlayInStatus.PLAYING,
      currentPos: startAt,
      currentPosTimestamp: date,
      pausedAt: null,
      remoteStatusRequest: null
    }

    addEntry('WatchService', 'PlayInStarted', 'Play-In playing', 'info')

    try {

      await this._doc.update(update)
      return true

    }
    catch (e) {

      addEntry('WatchService', 'PlayInStarted', `Error starting playin: ${e}`)
      return false

    }

  }

  /**
   * Sets the playIn to paused status and saves the last position
   * @param pausedAt The position - in seconds - to pause at
   */
  async pause (pausedAt: number): Promise<boolean> {

    const date = new Date(Date.now()).toISOString().substr(0, 19)
    const update = {
      currentPos: pausedAt,
      currentPosTimestamp: date,
      pausedAt,
      status: PlayInStatus.PAUSED,
      remoteStatusRequest: null
    }

    addEntry('WatchService', 'PlayInPaused', 'Play-In paused', 'info')

    try {

      await this._doc.update(update)
      return true

    }
    catch (e) {

      addEntry('WatchService', 'PlayInPaused', `Play-In paused error: ${e}`)
      return false

    }

  }

  /**
   * Set the playIn as inactive, maintaining the last position viewed
   * @param pausedAt The position - in seconds - to pause at
   */
  async inactive (pausedAt?: number): Promise<boolean> {

    if (!pausedAt) pausedAt = this.doc.currentPos

    const date = new Date(Date.now()).toISOString().substr(0, 19)
    const update = {
      currentPos: pausedAt,
      currentPosTimestamp: date,
      pausedAt,
      status: PlayInStatus.INACTIVE,
      remoteStatusRequest: null
    }

    try {

      await this._doc.update(update)
      return true

    }
    catch (e) {

      addEntry('WatchService', 'PlayInPaused', `Play-In inactive error: ${e}`)
      return false

    }

  }

  /** Reset the state on completion */
  async complete (): Promise<boolean> {

    addEntry('WatchService', 'PlayInEnd', 'Play-In complete', 'info')

    try {

      await this._doc.set(defaultState)
      return true

    }
    catch (e) {

      addEntry('WatchService', 'PlayInEnd', `Play-In complete error: ${e}`)
      return false

    }

  }

  /**
   * Sets the current position of the video
   * @param pos The current position - in seconds - of the video
   */
  async setCurrentPos (pos: number): Promise<boolean> {

    if (isEmpty(this.doc)) return true

    try {

      await this._doc.update({
        currentPos: pos,
        currentPosTimestamp: new Date(Date.now()).toISOString().substr(0, 19)
      })
      return true

    }
    catch (e) {

      addEntry('WatchService', 'PlayInSeeked', `Play-In set current position error: ${e}`)
      return false

    }

  }

  /**
   * Updates the document with the current playin state on play, pause or a completed seek
   * @param remoteStatusRequest The status to set
   * @param position The target position ( sent for pause or seek)
   * @param resumePlaying
   * */
  async setRemoteStatus (remoteStatusRequest: PlayInRemoteStatus, position?: number, resumePlaying?: boolean): Promise<number> {

    const requestId = this.doc.latestAppliedRequest + 1
    try {

      await this._doc.update({
        remoteStatusRequest: {
          status: remoteStatusRequest,
          value: position,
          requestId: requestId,
          resumePlaying: resumePlaying
        }
      })
      return requestId

    }
    catch (e) {

      addEntry('WatchService', 'PlayInRemoteStatus', `Play-In set remote status error: ${e}`)
      return -1

    }

  }

}

export default PlayIn
